import React, { useState, useCallback, useRef } from 'react';
import { useMutation } from 'react-query';
import { useNavigate, Link } from 'react-router-dom';
import {
  ArrowUpTrayIcon,
  DocumentTextIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
  ClipboardDocumentIcon,
  ArrowLeftIcon,
} from '@heroicons/react/24/outline';
import { standardsAPI } from '../services/standardsApi';
import { toast } from 'react-hot-toast';
import { getApiErrorMessage } from '../utils/apiError';

// Copy of the main app's client/src/pages/ImportStandardPage.js (plan 18.3), trimmed to
// Paste JSON and Upload File (JSON only) — no PDF/AI extraction tabs, so the PDF dropzone,
// LEED/WELL option constants, and token-usage panel are all dropped. `linkage_mode` is fixed
// to "manual" and the automatic/manual picker is hidden (18.3), since this demo has no
// linkage service to run automatic matching against.
//
// Build decision: the main app's file-drop UI uses react-dropzone, which is not a dependency
// of this demo's client (package.json is owned by a different phase and outside this file
// list), so file upload here is a plain <input type="file" accept=".json"> with a manual
// dragover/drop handler instead — same behavior (drag-and-drop or click to pick), no library.
const ImportStandardPage = () => {
  const navigate = useNavigate();
  const [jsonInput, setJsonInput] = useState('');
  const [inputMode, setInputMode] = useState('paste'); // 'paste' | 'file'
  const [isDragActive, setIsDragActive] = useState(false);
  const [fileName, setFileName] = useState(null);
  const [parseError, setParseError] = useState(null);
  const [parsedData, setParsedData] = useState(null);
  const [importResult, setImportResult] = useState(null);
  const fileInputRef = useRef(null);

  // Import mutation
  const importMutation = useMutation(
    (data) => standardsAPI.import(data),
    {
      onSuccess: (response) => {
        setImportResult(response.data);
        toast.success('Standard imported successfully!');
      },
      onError: (error) => {
        toast.error(getApiErrorMessage(error, 'Failed to import standard'));
      },
    }
  );

  // Parse JSON and validate
  const parseAndValidate = useCallback((jsonString) => {
    setParseError(null);
    setParsedData(null);

    if (!jsonString.trim()) {
      return;
    }

    try {
      const data = JSON.parse(jsonString);

      // Basic validation
      if (!data.name || typeof data.name !== 'string') {
        throw new Error('Missing or invalid "name" field');
      }
      if (!data.version || typeof data.version !== 'string') {
        throw new Error('Missing or invalid "version" field');
      }
      if (!data.requirements || !Array.isArray(data.requirements) || data.requirements.length === 0) {
        throw new Error('Missing or empty "requirements" array');
      }

      // Validate requirements
      const validateRequirement = (req, path = '') => {
        const reqPath = path ? `${path} > ${req.section}` : req.section;
        if (!req.section) throw new Error(`Missing "section" in requirement at ${reqPath || 'root'}`);
        if (!req.title) throw new Error(`Missing "title" in requirement ${reqPath}`);
        if (!req.description) throw new Error(`Missing "description" in requirement ${reqPath}`);
        if (!req.criteria) throw new Error(`Missing "criteria" in requirement ${reqPath}`);

        if (req.children && Array.isArray(req.children)) {
          req.children.forEach(child => validateRequirement(child, reqPath));
        }
      };

      data.requirements.forEach(req => validateRequirement(req));

      // Count total requirements including children
      const countRequirements = (reqs) => {
        return reqs.reduce((count, req) => {
          return count + 1 + (req.children ? countRequirements(req.children) : 0);
        }, 0);
      };

      const allRequirementsCount = countRequirements(data.requirements);
      const topLevelCount = data.requirements.length;
      const childCount = allRequirementsCount - topLevelCount;

      setParsedData({
        ...data,
        _meta: {
          totalRequirements: topLevelCount,
          topLevelCount,
          childCount,
        },
      });
    } catch (e) {
      setParseError(e.message);
    }
  }, []);

  // Handle JSON input change
  const handleJsonChange = (e) => {
    const value = e.target.value;
    setJsonInput(value);
    parseAndValidate(value);
    setImportResult(null);
  };

  const loadFile = useCallback((file) => {
    if (!file) return;
    setFileName(file.name);
    const reader = new FileReader();
    reader.onload = (e) => {
      const content = e.target.result;
      setJsonInput(content);
      parseAndValidate(content);
      setImportResult(null);
    };
    reader.readAsText(file);
  }, [parseAndValidate]);

  const handleFileInputChange = (e) => {
    loadFile(e.target.files?.[0]);
  };

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setIsDragActive(false);
    const file = e.dataTransfer?.files?.[0];
    if (file && (file.type === 'application/json' || file.name.endsWith('.json'))) {
      loadFile(file);
    }
  }, [loadFile]);

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragActive(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragActive(false);
  };

  // Handle import
  const handleImport = () => {
    if (parsedData) {
      // Remove _meta before sending; linkage_mode is always "manual" in this demo (18.3).
      const { _meta, ...dataToSend } = parsedData;
      importMutation.mutate({ ...dataToSend, linkage_mode: 'manual' });
    }
  };

  // Clear form
  const handleClear = () => {
    setJsonInput('');
    setParsedData(null);
    setParseError(null);
    setImportResult(null);
    setFileName(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // 18.3: "Load Sample loads the GRIHA JSON". It loaded a hand-written three-row excerpt
  // instead, so P4's exit check - import a JSON copy of GRIHA as a second standard - could
  // not be performed from the page the plan assigns to it. Fetching the seeded standard is
  // better than shipping a static copy: there is one source of truth, it cannot drift from
  // what the seeder writes, and it exercises the exact round trip an operator performs
  // (paste GET /standards/{id}'s output into Import), which the API re-nests from its flat
  // parent_id form.
  const [loadingSample, setLoadingSample] = useState(false);

  const loadSampleJson = async () => {
    setLoadingSample(true);
    try {
      const listed = await standardsAPI.getAll({ page_size: 200 });
      const griha = (listed.data.standards || []).find(
        (s) => s.name.toUpperCase() === 'GRIHA'
      );
      if (!griha) {
        toast.error('No GRIHA standard is seeded to copy from');
        return;
      }
      const detail = await standardsAPI.getById(griha.id);
      setJsonInput(
        JSON.stringify(
          {
            name: 'GRIHA (imported copy)',
            version: `${griha.version}-copy`,
            description: `Copy of ${griha.name} ${griha.version}, loaded from this instance.`,
            linkage_mode: 'manual',
            requirements: detail.data.requirements,
          },
          null,
          2
        )
      );
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Could not load the GRIHA standard'));
    } finally {
      setLoadingSample(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F8F8FF]">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <Link
            to="/standards"
            className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700 mb-4"
          >
            <ArrowLeftIcon className="h-4 w-4 mr-1" />
            Back to standards
          </Link>
          <h1 className="text-2xl font-semibold text-gray-900">Import Certification Standard</h1>
          <p className="mt-1 text-sm text-gray-500">
            Add a new standard by pasting JSON or uploading a JSON file. Review the result below, then click Import Standard to save.
          </p>
        </div>

        {/* Tabs: Paste JSON | Upload File */}
        <div className="mb-6">
          <div className="flex space-x-4">
            <button
              onClick={() => { setInputMode('paste'); handleClear(); }}
              className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                inputMode === 'paste'
                  ? 'bg-[#7C3AED] text-white'
                  : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
              }`}
            >
              <ClipboardDocumentIcon className="h-4 w-4 inline mr-2" />
              Paste JSON
            </button>
            <button
              onClick={() => { setInputMode('file'); handleClear(); }}
              className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                inputMode === 'file'
                  ? 'bg-[#7C3AED] text-white'
                  : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
              }`}
            >
              <ArrowUpTrayIcon className="h-4 w-4 inline mr-2" />
              Upload File
            </button>
            <button
              onClick={loadSampleJson}
              disabled={loadingSample}
              className="px-4 py-2 text-sm font-medium text-[#7C3AED] hover:text-[#6D28D9] transition-colors disabled:opacity-50"
            >
              {loadingSample ? 'Loading GRIHA…' : 'Load Sample'}
            </button>
          </div>
        </div>

        {/* Input Area */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6">
          {inputMode === 'paste' ? (
            <div className="p-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                JSON Data
              </label>
              <textarea
                value={jsonInput}
                onChange={handleJsonChange}
                placeholder='{"name": "Standard Name", "version": "1.0", "requirements": [...]}'
                className="w-full h-80 px-3 py-2 border border-gray-300 rounded-lg font-mono text-sm focus:ring-2 focus:ring-[#7C3AED] focus:border-transparent resize-none"
              />
            </div>
          ) : (
            <div className="p-4">
              <div
                onClick={() => fileInputRef.current?.click()}
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                role="button"
                tabIndex={0}
                className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
                  isDragActive
                    ? 'border-[#7C3AED] bg-purple-50'
                    : 'border-gray-300 hover:border-[#7C3AED] hover:bg-gray-50'
                }`}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="application/json,.json"
                  onChange={handleFileInputChange}
                  className="hidden"
                />
                <ArrowUpTrayIcon className="h-12 w-12 mx-auto text-gray-400 mb-4" />
                {isDragActive ? (
                  <p className="text-[#7C3AED] font-medium">Drop the JSON file here...</p>
                ) : fileName ? (
                  <p className="text-[#7C3AED] font-medium">{fileName}</p>
                ) : (
                  <>
                    <p className="text-gray-600 mb-1">Drag and drop a JSON file here, or click to select</p>
                    <p className="text-sm text-gray-400">.json only</p>
                  </>
                )}
              </div>
              {jsonInput && (
                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    File Contents
                  </label>
                  <pre className="w-full h-60 px-3 py-2 border border-gray-300 rounded-lg font-mono text-sm bg-gray-50 overflow-auto">
                    {jsonInput}
                  </pre>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Parse Error */}
        {parseError && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start">
            <ExclamationCircleIcon className="h-5 w-5 text-red-500 mt-0.5 mr-3 flex-shrink-0" />
            <div>
              <h4 className="text-sm font-medium text-red-800">Invalid JSON</h4>
              <p className="text-sm text-red-600 mt-1">{parseError}</p>
            </div>
          </div>
        )}

        {/* Parsed Preview */}
        {parsedData && !importResult && (
          <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
            <div className="flex items-start">
              <CheckCircleIcon className="h-5 w-5 text-green-500 mt-0.5 mr-3 flex-shrink-0" />
              <div className="flex-1">
                <h4 className="text-sm font-medium text-green-800">Valid — ready to import</h4>
                <div className="mt-3 grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-gray-500">Standard:</span>
                    <span className="ml-2 font-medium text-gray-900">
                      {parsedData.name} v{parsedData.version}
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-500">Total Requirements:</span>
                    <span className="ml-2 font-medium text-gray-900">
                      {parsedData._meta.totalRequirements}
                    </span>
                  </div>
                  {parsedData.description && (
                    <div className="col-span-2">
                      <span className="text-gray-500">Description:</span>
                      <span className="ml-2 text-gray-700">{parsedData.description}</span>
                    </div>
                  )}
                  <div>
                    <span className="text-gray-500">Top-level Requirements:</span>
                    <span className="ml-2 text-gray-700">{parsedData._meta.topLevelCount}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Child Requirements:</span>
                    <span className="ml-2 text-gray-700">{parsedData._meta.childCount}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Import Result */}
        {importResult && (
          <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex items-start">
              <CheckCircleIcon className="h-5 w-5 text-blue-500 mt-0.5 mr-3 flex-shrink-0" />
              <div className="flex-1">
                <h4 className="text-sm font-medium text-blue-800">Import Completed Successfully</h4>
                <div className="mt-3 grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-gray-500">Standard:</span>
                    <span className="ml-2 font-medium text-gray-900">
                      {importResult.standard_name} v{importResult.standard_version}
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-500">Status:</span>
                    <span className={`ml-2 px-2 py-0.5 rounded text-xs font-medium ${
                      importResult.is_new_standard
                        ? 'bg-green-100 text-green-800'
                        : 'bg-yellow-100 text-yellow-800'
                    }`}>
                      {importResult.is_new_standard ? 'New Standard Created' : 'Added to Existing'}
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-500">Requirements Created:</span>
                    <span className="ml-2 font-medium text-green-600">
                      {importResult.requirements_created}
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-500">Requirements Skipped:</span>
                    <span className="ml-2 font-medium text-gray-600">
                      {importResult.requirements_skipped}
                    </span>
                  </div>
                </div>
                <div className="mt-4 flex flex-wrap items-center gap-4">
                  <button
                    onClick={() => navigate('/standards')}
                    className="text-sm text-blue-600 hover:text-blue-800 font-medium"
                  >
                    View Standards &rarr;
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="flex justify-end space-x-4">
          <button
            onClick={handleClear}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Clear
          </button>
          <button
            onClick={() => navigate(-1)}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleImport}
            disabled={!parsedData || importMutation.isLoading}
            className={`px-6 py-2 text-sm font-medium rounded-lg transition-colors flex items-center ${
              parsedData && !importMutation.isLoading
                ? 'bg-[#7C3AED] text-white hover:bg-[#6D28D9]'
                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
            }`}
          >
            {importMutation.isLoading ? (
              <>
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                Importing...
              </>
            ) : (
              <>
                <DocumentTextIcon className="h-4 w-4 mr-2" />
                Import Standard
              </>
            )}
          </button>
        </div>

        {/* JSON Structure Reference */}
        <div className="mt-8 p-4 bg-gray-50 rounded-lg border border-gray-200">
          <h3 className="text-sm font-medium text-gray-900 mb-2">Expected JSON Structure</h3>
          <pre className="text-xs text-gray-600 overflow-x-auto">
{`{
  "name": "Standard Name",
  "version": "1.0",
  "description": "Optional description",
  "requirements": [
    {
      "section": "1",
      "title": "Requirement Title",
      "description": "Detailed description",
      "criteria": "Assessment criteria",
      "mandatory": true,
      "weight": 1.0,
      "children": [
        {
          "section": "1.1",
          "subsection": "1",
          "title": "Sub-requirement",
          "description": "...",
          "criteria": "..."
        }
      ]
    }
  ]
}`}
          </pre>
        </div>
      </div>
    </div>
  );
};

export default ImportStandardPage;
